import { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";

const s3TimeoutDuration = dayjs.duration(30, "second").asMilliseconds();

const INITIAL_LOADING_STATE = {
  status: "loading" as const,
  data: null,
  error: null,
} as const;

type LoadingState = typeof INITIAL_LOADING_STATE;
type ErrorState = {
  status: "error";
  data: null;
  error: Error;
};
type SuccessState<T> = {
  status: "success";
  data: T;
  error: null;
};

type HookState<T> = LoadingState | ErrorState | SuccessState<T>;

const useRemoteJsonFile = <T>(url: string | null) => {
  const [state, setState] = useState<HookState<T>>(INITIAL_LOADING_STATE);

  useEffect(() => {
    if (!url) {
      setState({
        status: "error",
        data: null,
        error: new Error("No URL provided in query params"),
      });
      return;
    } else if (!url.toLowerCase().startsWith("https://")) {
      setState({
        status: "error",
        data: null,
        error: new Error("URL must start with HTTPS for security"),
      });
      return;
    }

    // Reset to loading state when URL changes
    setState(INITIAL_LOADING_STATE);

    const fetchJson = async (url: string) => {
      try {
        // const response = await fetch(url);
        const apiS3 = axios.create();
        apiS3.defaults.timeout = s3TimeoutDuration;
        const response = await apiS3.get(url);

        if (response.status !== 200) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers["content-type"];
        if (
          !contentType ||
          (!contentType.includes("application/json") && !contentType.includes("application/gzip"))
        ) {
          throw new Error("Response must be either JSON or GZIP");
        }

        const jsonData = response.data;
        setState({
          status: "success",
          data: jsonData,
          error: null,
        });
      } catch (err) {
        setState({
          status: "error",
          data: null,
          error: err instanceof Error ? err : new Error("Unknown error occurred"),
        });
      }
    };

    fetchJson(url);
  }, [url]);

  return state;
};

export default useRemoteJsonFile;
